import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { aiAuthFetch } from "./aiAuthFetch";
import { buildHisab, num } from "./campus_hisab_calc";
import { readReceiptQr } from "./campus_receipt_read";
import {
    CAMPUS_API_BASE, Chips, Stat, fmtDay, fmtINR, fmtNum, mediaUrl, scopeLabel, useQrPayments,
} from "./campus_hisab_ui";

const VIEWS = [
    { id: "hisab", label: "Hisab" },
    { id: "overview", label: "Overview" },
    { id: "staff", label: "Staff" },
    { id: "bills", label: "Bill by bill" },
    { id: "problems", label: "Needs checking" },
];

const SCOPES = [
    { id: "collection", label: "Money received in these dates" },
    { id: "orders", label: "This drive's orders, start to end" },
];

const BILL_FILTERS = [
    { id: "short", label: "QR missing" },
    { id: "due", label: "Balance due" },
    { id: "cash", label: "Cash taken" },
    { id: "all", label: "All bills" },
];

const COL_PICKS = "CampusQrPicks";
const KIND_LABEL = { advance: "Booking advance", delivery: "Delivery balance" };

// Booking-advance receipts live outside the light read; fetch the URL only
// when someone actually opens the popup, and remember it for the session.
const receiptUrlCache = new Map();

async function receiptUrlFor(line, bookingId) {
    const direct = mediaUrl(line.receipt);
    if (direct) return direct;
    if (line.kind !== "advance" || !bookingId) return "";
    if (receiptUrlCache.has(bookingId)) return receiptUrlCache.get(bookingId);
    const res = await aiAuthFetch(`${CAMPUS_API_BASE}/bookings/${bookingId}`);
    const data = res.ok ? await res.json() : {};
    const b = data.booking || data;
    const url = mediaUrl(typeof b.advance_receipt === "string" ? b.advance_receipt : "");
    receiptUrlCache.set(bookingId, url);
    return url;
}

function qrIsKnown(line) {
    return line.qrFrom === "done" || line.qrFrom === "picked" || line.qrFrom === "receipt";
}

function PayChip({ line, onOpen }) {
    if (line.mode === "cash") {
        return (
            <span className="campus-paychip is-cash">
                {line.kind === "advance" ? "Advance" : "Balance"} cash {fmtINR(line.amount)}
                <b>{line.holder || "—"} ke paas</b>
            </span>
        );
    }
    if (qrIsKnown(line)) {
        return (
            <span className="campus-paychip is-ok">
                {line.kind === "delivery" && line.holder ? <b>Delivered by {line.holder}</b> : null}
                {line.kind === "advance" ? "Advance" : "Balance"} online {fmtINR(line.amount)}
                <b>→ {line.qrName || "QR"}</b>
            </span>
        );
    }
    return (
        <span className="campus-paychip is-short">
            {line.kind === "delivery" && line.holder ? <b>Delivered by {line.holder}</b> : null}
            {line.kind === "advance" ? "Advance" : "Balance"} online {fmtINR(line.amount)}
            <b>QR missing</b>
            <button type="button" className="campus-paychip-btn" onClick={() => onOpen(line)}>Receipt</button>
        </span>
    );
}

function sideOf(lines, kind) {
    const cash = new Map();
    const qr = new Map();
    const open = [];
    let cashSum = 0;
    let online = 0;
    lines.forEach((l) => {
        if (l.kind !== kind) return;
        if (l.mode === "cash") {
            cashSum += l.amount;
            const who = l.holder || "Unknown";
            cash.set(who, (cash.get(who) || 0) + l.amount);
        } else {
            online += l.amount;
            if (qrIsKnown(l) && l.qrName) {
                qr.set(l.qrName, (qr.get(l.qrName) || 0) + l.amount);
            } else {
                open.push(l);
            }
        }
    });
    const byAmount = (a, b) => b[1] - a[1];
    return {
        cashSum,
        online,
        total: cashSum + online,
        cash: [...cash.entries()].sort(byAmount),
        qr: [...qr.entries()].sort(byAmount),
        open,
        openSum: open.reduce((a, l) => a + l.amount, 0),
    };
}

function HisabSide({ title, note, tone, side, onOpen }) {
    const qrKnown = side.qr.reduce((a, pair) => a + pair[1], 0);
    return (
        <section className={`campus-side is-${tone}`}>
            <header className="campus-side-head">
                <div>
                    <strong>{title}</strong>
                    <span>{note}</span>
                </div>
                <em>{fmtINR(side.total)}</em>
            </header>
            <div className="campus-side-body">
                <div className="campus-side-label">Cash in hand · {fmtINR(side.cashSum)}</div>
                {side.cash.length ? (
                    <ul className="campus-side-list">
                        {side.cash.map(([name, amount]) => (
                            <li key={name}><span>{name}</span><b>{fmtINR(amount)}</b></li>
                        ))}
                    </ul>
                ) : <p className="campus-note">Is waqt cash nahi aaya.</p>}

                <div className="campus-side-label">Online on QR · {fmtINR(qrKnown)}</div>
                {side.qr.length ? (
                    <ul className="campus-side-list">
                        {side.qr.map(([name, amount]) => (
                            <li key={name}><span>{name}</span><b>{fmtINR(amount)}</b></li>
                        ))}
                    </ul>
                ) : <p className="campus-note">Koi online QR pe confirm nahi.</p>}

                {side.open.length > 0 && (
                    <>
                        <div className="campus-side-label is-short">QR naam baaki · {fmtINR(side.openSum)}</div>
                        <ul className="campus-side-list is-short">
                            {side.open.map((l) => (
                                <li key={l.pickKey || l.id}>
                                    <span>{l.invoice} · {fmtINR(l.amount)} · {l.holder || "—"}</span>
                                    <button type="button" className="campus-paychip-btn" onClick={() => onOpen(l)}>Receipt</button>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </section>
    );
}

function BillLine({ row, onOpen }) {
    return (
        <article className={`campus-billline ${row.short > 0 ? "is-short" : ""}`}>
            <div className="campus-billline-id">
                <strong>{row.invoice}</strong>
                {row.short > 0 && <em>Short</em>}
            </div>
            <div className="campus-billline-who">
                <span className="campus-billline-name">{row.customer}</span>
                <span>Booked by <b>{row.bookedBy || "—"}</b></span>
            </div>
            <div className="campus-billline-money">
                <span>{fmtINR(row.total)}</span>
                {row.due > 0 && <em className="is-due">Due {fmtINR(row.due)}</em>}
            </div>
            <div className="campus-billline-pays">
                {row.lines.length === 0 && <span className="campus-hisab-sub is-muted">Abhi koi payment nahi</span>}
                {row.lines.map((l) => (
                    <PayChip key={l.pickKey || l.id} line={l} onOpen={onOpen} />
                ))}
            </div>
        </article>
    );
}

function ReceiptPopup({ popup, qrNames, saving, onClose, onPick }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const line = popup.line;
    const names = popup.chosen && !qrNames.includes(popup.chosen) ? [popup.chosen, ...qrNames] : qrNames;
    const label = `${line.kind === "advance" ? "Advance" : "Balance"} online ${fmtINR(line.amount)}`;

    return (
        <div className="campus-pop" onClick={onClose} role="presentation">
            <div className="campus-pop-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <header className="campus-pop-head">
                    <div>
                        <strong>{line.invoice}</strong>
                        <span>{line.customer} · {label}</span>
                    </div>
                    <button type="button" className="campus-pop-x" onClick={onClose}>Close</button>
                </header>
                <div className="campus-pop-body">
                    <div className="campus-pop-shot">
                        {popup.loading && <span>Receipt aa rahi hai…</span>}
                        {!popup.loading && popup.url && <img src={popup.url} alt={`${line.invoice} receipt`} />}
                        {!popup.loading && !popup.url && <span>{popup.error || "Is payment pe receipt save nahi hai."}</span>}
                    </div>
                    <aside className="campus-pop-side">
                        <div className="campus-pop-ask">
                            {popup.reading
                                ? "Screenshot gehrai se padh raha hai…"
                                : "Screenshot se naam padha. Galat ho to doosra dabao."}
                        </div>
                        <div className="campus-pop-names">
                            {names.length ? names.map((n) => (
                                <button
                                    type="button"
                                    key={n}
                                    className={`campus-pop-name ${popup.chosen === n ? "is-on" : ""}`}
                                    disabled={saving}
                                    onClick={() => onPick(line, n)}
                                >
                                    {n}
                                </button>
                            )) : <span>QR list load nahi hui. Refresh QR dabao.</span>}
                        </div>
                        {popup.chosen && <p className="campus-pop-saved">{popup.chosen} save ho gaya.</p>}
                    </aside>
                </div>
            </div>
        </div>
    );
}

export default function CampusHisab({ bookings = [], drive = {} }) {
    const { rows: qrRows, state: qrState, refresh: refreshQr } = useQrPayments();
    const [scopeMode, setScopeMode] = useState("orders");
    const [view, setView] = useState("hisab");
    const [billFilter, setBillFilter] = useState("all");
    const [who, setWho] = useState("");
    const [search, setSearch] = useState("");
    const [picks, setPicks] = useState({});
    const [savingKey, setSavingKey] = useState("");
    const [popup, setPopup] = useState(null);
    const [readRun, setReadRun] = useState(null);
    const orderMode = scopeMode === "orders";

    useEffect(() => {
        const unsub = db.collection(COL_PICKS).onSnapshot(
            (snap) => {
                const next = {};
                snap.forEach((d) => { next[d.id] = (d.data() && d.data().qrName) || ""; });
                setPicks(next);
            },
            (e) => console.log("qr picks", e)
        );
        return unsub;
    }, []);

    const hisab = useMemo(
        () => buildHisab(bookings, qrRows, drive, scopeMode, picks),
        [bookings, qrRows, drive, scopeMode, picks]
    );
    const t = hisab.totals;

    const deliverySplitMissing = useMemo(() => {
        if (!bookings.length) return false;
        return bookings.some((b) => num(b && b.balance_paid) > 0) && !bookings.some((b) => b && b.delivery_log);
    }, [bookings]);

    const visibleRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return hisab.rows.filter((r) => {
            if (billFilter === "short" && r.short <= 0) return false;
            if (billFilter === "due" && r.due <= 0) return false;
            if (billFilter === "cash" && r.cash <= 0) return false;
            if (who) {
                const names = [r.bookedBy, ...r.deliverers, ...r.lines.map((l) => l.holder), ...r.qrStaff]
                    .filter(Boolean).map((n) => n.toLowerCase());
                if (!names.includes(who.toLowerCase())) return false;
            }
            if (!q) return true;
            const hay = [r.invoice, r.customer, r.mobile, r.bookedBy,
                ...r.lines.map((l) => `${l.holder} ${l.qrName} ${l.alsoQr}`)].join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [hisab.rows, billFilter, who, search]);

    const qrNames = useMemo(() => {
        const names = new Set();
        qrRows.forEach((q) => { if (q && q.qr_name) names.add(q.qr_name); });
        Object.values(picks).forEach((n) => { if (n) names.add(n); });
        return [...names].sort((a, b) => a.localeCompare(b));
    }, [qrRows, picks]);

    const savePick = async (line, qrName) => {
        if (!line.pickKey || !qrName) return;
        setSavingKey(line.pickKey);
        try {
            await db.collection(COL_PICKS).doc(line.pickKey).set({
                qrName,
                invoice: line.invoice || "",
                kind: line.kind || "",
                amount: line.amount || 0,
                at: Date.now(),
            });
            setPopup((p) => (p && p.line.pickKey === line.pickKey ? { ...p, chosen: qrName } : p));
        } catch (e) {
            console.log("save qr pick", e);
            alert("QR save nahi hua. Dobara try karo.");
        } finally {
            setSavingKey("");
        }
    };

    const openReceipt = async (line, bookingId) => {
        setPopup({
            line,
            url: "",
            loading: true,
            reading: false,
            error: "",
            chosen: qrIsKnown(line) ? line.qrName : "",
        });
        let url = "";
        try {
            url = await receiptUrlFor(line, bookingId);
            setPopup((p) => (p && p.line.pickKey === line.pickKey
                ? { ...p, url, loading: false, reading: !!url, error: url ? "" : "Is payment pe receipt save nahi hai." }
                : p));
        } catch (e) {
            console.log("receipt", e);
            setPopup((p) => (p && p.line.pickKey === line.pickKey
                ? { ...p, loading: false, error: "Receipt load nahi hui. Dobara try karo." }
                : p));
            return;
        }
        if (!url) return;
        try {
            const name = await readReceiptQr(url, qrNames);
            setPopup((p) => (p && p.line.pickKey === line.pickKey ? { ...p, reading: false } : p));
            if (name) await savePick(line, name);
        } catch (e) {
            console.log("read receipt", e);
            setPopup((p) => (p && p.line.pickKey === line.pickKey
                ? { ...p, reading: false, error: "Screenshot padha nahi gaya. Naam khud chuno." }
                : p));
        }
    };

    const readShort = async () => {
        const jobs = [];
        visibleRows.forEach((r) => {
            r.lines.forEach((l) => {
                if (l.mode === "online" && !qrIsKnown(l)) {
                    jobs.push({ line: l, bookingId: r.bookingId });
                }
            });
        });
        if (!jobs.length) return;
        setReadRun({ done: 0, total: jobs.length, found: 0 });
        let found = 0;
        for (let i = 0; i < jobs.length; i += 1) {
            const { line, bookingId } = jobs[i];
            try {
                const url = await receiptUrlFor(line, bookingId);
                const name = url ? await readReceiptQr(url, qrNames) : "";
                if (name) {
                    await savePick(line, name);
                    found += 1;
                }
            } catch (e) {
                console.log("bulk read", e);
            }
            setReadRun({ done: i + 1, total: jobs.length, found });
        }
    };

    const billCounts = useMemo(() => ({
        short: hisab.rows.filter((r) => r.short > 0).length,
        due: hisab.rows.filter((r) => r.due > 0).length,
        cash: hisab.rows.filter((r) => r.cash > 0).length,
        all: hisab.rows.length,
    }), [hisab.rows]);

    const issues = hisab.issues;
    const problemCount = issues.shortLines.length + issues.cashConflicts.length
        + issues.zeroDone.length + issues.looseQr.length + issues.extraQr.length;

    const openBills = (person) => {
        setWho(person);
        setBillFilter(orderMode ? "all" : "short");
        setView("bills");
    };

    return (
        <div className="campus-hisab">
            <div className="campus-hisab-head">
                <div>
                    <div className="campus-table-title">{orderMode ? "Orders" : "Collection"} — {scopeLabel(drive)}</div>
                    <p className="campus-hisab-lead">
                        {orderMode
                            ? "Booking ka hisaab alag hai, delivery ka alag. Cash us staff ke paas jo us waqt le kar aaya. Online us QR pe gaya jiska naam screenshot se save hua."
                            : "Every payment that landed in these dates, whichever order it belongs to. Cash sits with whoever took it — booking cash with the booker, delivery cash with the person who delivered. Online has to land on a QR, and the QR name is saved only when Done is pressed."}
                        {!orderMode && hisab.olderDeliveries > 0 && ` ${fmtNum(hisab.olderDeliveries)} of these payments are older orders whose balance came inside these dates.`}
                    </p>
                </div>
                <button type="button" className="campus-orders-refresh" onClick={refreshQr} disabled={qrState === "loading"}>
                    {qrState === "loading" ? "Loading QR…" : "↻ Refresh QR"}
                </button>
            </div>

            <div className="campus-hisab-nav">
                <Chips
                    options={SCOPES}
                    value={scopeMode}
                    onChange={(m) => {
                        setScopeMode(m);
                        setBillFilter(m === "orders" ? "all" : "short");
                        if (m === "orders") setView("hisab");
                    }}
                />
            </div>

            {deliverySplitMissing && (
                <p className="campus-hisab-warn">Delivery cash and online are not in this load yet. Refresh the page once.</p>
            )}
            {qrState === "error" && (
                <p className="campus-hisab-warn">QR list could not be loaded, so every online payment below looks unconfirmed. Try Refresh QR.</p>
            )}
            {hisab.qrLoaded >= 500 && hisab.qrOldest && (
                <p className="campus-hisab-warn">
                    The QR log only returns the latest 500 payments — currently back to {fmtDay(hisab.qrOldest)}.
                    A drive older than that will show QR as missing even when Done was pressed.
                </p>
            )}

            <div className="campus-stat-row">
                {orderMode && <Stat label="Order value" value={fmtINR(t.orderValue)} sub={`${fmtNum(t.bills)} orders`} />}
                <Stat
                    label={orderMode ? "Paid on these orders" : "Collected"}
                    value={fmtINR(t.collected)}
                    sub={`${fmtNum(t.payments)} payments${orderMode ? "" : ` · ${fmtNum(t.bills)} bills`}`}
                    tone="accent"
                />
                <Stat label="Cash in hand" value={fmtINR(t.cash)} sub="With the staff, not banked" />
                <Stat label="Online" value={fmtINR(t.online)} />
                <Stat label="Confirmed on a QR" value={fmtINR(t.matchedQr)} sub={`QR log total ${fmtINR(t.qr)}`} tone="good" />
                <Stat label="QR missing" value={fmtINR(t.short)} sub={`${fmtNum(t.shortBills)} bills`} tone={t.short > 0 ? "bad" : "good"} />
                <Stat label="Balance still due" value={fmtINR(t.due)} tone={t.due > 0 ? "bad" : undefined} />
            </div>

            <div className="campus-hisab-nav">
                <Chips
                    options={VIEWS.map((v) => (v.id === "problems" ? { ...v, count: problemCount } : v))}
                    value={view}
                    onChange={setView}
                />
                {who && (
                    <button type="button" className="campus-hisab-clear" onClick={() => setWho("")}>
                        Only {who} ✕
                    </button>
                )}
            </div>

            {view === "hisab" && (
                <div className="campus-hisab-split">
                    <HisabSide
                        title="Booking hisab"
                        note="Jab booking karke aate hain"
                        tone="book"
                        side={sideOf(hisab.lines, "advance")}
                        onOpen={(line) => {
                            const row = hisab.rows.find((r) => r.invoice === line.invoice);
                            openReceipt(line, row && row.bookingId);
                        }}
                    />
                    <HisabSide
                        title="Delivery hisab"
                        note="Jab deliver karne jaate hain"
                        tone="deliver"
                        side={sideOf(hisab.lines, "delivery")}
                        onOpen={(line) => {
                            const row = hisab.rows.find((r) => r.invoice === line.invoice);
                            openReceipt(line, row && row.bookingId);
                        }}
                    />
                </div>
            )}

            {view === "overview" && (
                <>
                    <div className="campus-table-title">Day by day</div>
                    <div className="campus-table-wrap">
                        <table className="campus-table">
                            <thead>
                                <tr>
                                    <th>Day</th>
                                    <th className="r">New bills</th>
                                    <th className="r">Advance cash</th>
                                    <th className="r">Advance online</th>
                                    <th className="r">Deliveries</th>
                                    <th className="r">Balance cash</th>
                                    <th className="r">Balance online</th>
                                    <th className="r">Collected</th>
                                    <th className="r">On QR</th>
                                </tr>
                            </thead>
                            <tbody>
                                {hisab.byDay.length ? hisab.byDay.map((d) => (
                                    <tr key={d.day}>
                                        <td><strong>{fmtDay(d.day)}</strong></td>
                                        <td className="r">{d.bookings || "—"}</td>
                                        <td className="r">{d.advCash ? fmtINR(d.advCash) : "—"}</td>
                                        <td className="r">{d.advOnline ? fmtINR(d.advOnline) : "—"}</td>
                                        <td className="r">{d.deliveries || "—"}</td>
                                        <td className="r">{d.balCash ? fmtINR(d.balCash) : "—"}</td>
                                        <td className="r">{d.balOnline ? fmtINR(d.balOnline) : "—"}</td>
                                        <td className="r"><strong>{fmtINR(d.cash + d.online)}</strong></td>
                                        <td className="r">{d.qr ? `${fmtINR(d.qr)} · ${fmtNum(d.qrCount)}` : "—"}</td>
                                    </tr>
                                )) : <tr><td colSpan={9} className="c">No collection in this drive yet.</td></tr>}
                            </tbody>
                            {hisab.byDay.length > 0 && (
                                <tfoot>
                                    <tr>
                                        <td><strong>Total</strong></td>
                                        <td className="r">{fmtNum(hisab.byDay.reduce((a, d) => a + d.bookings, 0))}</td>
                                        <td className="r">{fmtINR(hisab.byDay.reduce((a, d) => a + d.advCash, 0))}</td>
                                        <td className="r">{fmtINR(hisab.byDay.reduce((a, d) => a + d.advOnline, 0))}</td>
                                        <td className="r">{fmtNum(hisab.byDay.reduce((a, d) => a + d.deliveries, 0))}</td>
                                        <td className="r">{fmtINR(hisab.byDay.reduce((a, d) => a + d.balCash, 0))}</td>
                                        <td className="r">{fmtINR(hisab.byDay.reduce((a, d) => a + d.balOnline, 0))}</td>
                                        <td className="r"><strong>{fmtINR(t.collected)}</strong></td>
                                        <td className="r">{fmtINR(t.qr)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>

                    <div className="campus-table-title">Which QR received the money</div>
                    <div className="campus-qr-cards">
                        {hisab.qrAccounts.length ? hisab.qrAccounts.map((a) => (
                            <div className="campus-qr-card" key={a.name}>
                                <strong>{a.name}</strong>
                                <span className="campus-qr-amt">{fmtINR(a.total)}</span>
                                <span className="campus-qr-cnt">{fmtNum(a.count)} payment{a.count > 1 ? "s" : ""}</span>
                                <span className="campus-qr-cnt">{a.days.map((d) => `${fmtDay(d.day)} ${fmtINR(d.total)}`).join(" · ")}</span>
                            </div>
                        )) : <div className="campus-orders-empty">No QR payments in this drive.</div>}
                    </div>
                    <p className="campus-note">Full QR log, with every bill it was tied to, is in the QR Payments tab.</p>
                </>
            )}

            {view === "staff" && (
                <>
                    <div className="campus-table-title">Cash to collect from staff</div>
                    <div className="campus-hisab-people">
                        {hisab.people.length ? hisab.people.map((p) => (
                            <button
                                type="button"
                                key={p.name}
                                className={`campus-hisab-person ${who.toLowerCase() === p.name.toLowerCase() ? "is-on" : ""} ${p.short > 0 ? "is-short" : ""}`}
                                onClick={() => openBills(who.toLowerCase() === p.name.toLowerCase() ? "" : p.name)}
                            >
                                <strong>{p.name}</strong>
                                <span className="campus-hisab-person-cash">Cash {fmtINR(p.cash)}</span>
                                <span>Online {fmtINR(p.online)}</span>
                                <span>QR under this name {fmtINR(p.qr)}</span>
                                <em>
                                    {p.short > 0
                                        ? `QR missing ${fmtINR(p.short)}`
                                        : p.online > 0 ? "All online on a QR" : "No online collected"}
                                </em>
                            </button>
                        )) : <div className="campus-orders-empty">No staff activity in this drive.</div>}
                    </div>
                    <p className="campus-note">Click a person to open their bills. Cash is what they should hand over. QR missing is online they collected with no QR amount saved on Done.</p>

                    <div className="campus-table-wrap">
                        <table className="campus-table">
                            <thead>
                                <tr>
                                    <th>Staff</th>
                                    <th className="r">Bookings</th>
                                    <th className="r">Deliveries</th>
                                    <th className="r">Booking cash</th>
                                    <th className="r">Delivery cash</th>
                                    <th className="r">Cash in hand</th>
                                    <th className="r">Online taken</th>
                                    <th className="r">QR under name</th>
                                    <th className="r">QR missing</th>
                                </tr>
                            </thead>
                            <tbody>
                                {hisab.people.map((p) => (
                                    <tr key={p.name} className={p.short > 0 ? "campus-row-short" : ""}>
                                        <td><strong>{p.name}</strong></td>
                                        <td className="r">{p.bookings || "—"}</td>
                                        <td className="r">{p.deliveries || "—"}</td>
                                        <td className="r">{p.bookingCash ? fmtINR(p.bookingCash) : "—"}</td>
                                        <td className="r">{p.deliveryCash ? fmtINR(p.deliveryCash) : "—"}</td>
                                        <td className="r"><strong>{p.cash ? fmtINR(p.cash) : "—"}</strong></td>
                                        <td className="r">{p.online ? fmtINR(p.online) : "—"}</td>
                                        <td className="r">{p.qr ? `${fmtINR(p.qr)} · ${fmtNum(p.qrCount)}` : "—"}</td>
                                        <td className="r">{p.short > 0 ? <strong className="campus-hisab-short">{fmtINR(p.short)}</strong> : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td><strong>Total</strong></td>
                                    <td className="r">{fmtNum(hisab.people.reduce((a, p) => a + p.bookings, 0))}</td>
                                    <td className="r">{fmtNum(hisab.people.reduce((a, p) => a + p.deliveries, 0))}</td>
                                    <td className="r">{fmtINR(hisab.people.reduce((a, p) => a + p.bookingCash, 0))}</td>
                                    <td className="r">{fmtINR(hisab.people.reduce((a, p) => a + p.deliveryCash, 0))}</td>
                                    <td className="r"><strong>{fmtINR(t.cash)}</strong></td>
                                    <td className="r">{fmtINR(t.online)}</td>
                                    <td className="r">{fmtINR(t.qr)}</td>
                                    <td className="r">{fmtINR(t.short)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    <p className="campus-note">A person can show QR more than online when a customer paid a QR in their name but Done was pressed by someone else. Open the QR Payments tab to see those rows.</p>
                </>
            )}

            {view === "bills" && (
                <>
                    <div className="campus-orders-toolbar campus-dist-toolbar">
                        <Chips
                            options={BILL_FILTERS.map((f) => ({ ...f, count: billCounts[f.id] }))}
                            value={billFilter}
                            onChange={setBillFilter}
                        />
                        <div className="campus-orders-search">
                            <input
                                type="search"
                                placeholder="Bill, customer, phone, staff, QR…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                autoComplete="off"
                            />
                        </div>
                    </div>
                    <p className="campus-note">
                        Showing {fmtNum(visibleRows.length)} of {fmtNum(t.bills)} {orderMode ? "orders" : "bills"}{who ? ` · only ${who}` : ""}.
                        Cash apne aap us staff ke paas. Jahan QR missing hai, Receipt dabao — screenshot se naam padh ke save ho jayega.
                        {" "}
                        <button
                            type="button"
                            className="campus-paychip-btn"
                            onClick={readShort}
                            disabled={!!readRun && readRun.done < readRun.total}
                        >
                            {readRun && readRun.done < readRun.total
                                ? `Padh raha hai ${readRun.done}/${readRun.total}`
                                : "Saari short receipts padho"}
                        </button>
                        {readRun && readRun.done === readRun.total && ` ${readRun.found} naam mil gaye.`}
                    </p>
                    {visibleRows.length
                        ? (
                            <div className="campus-bill-list">
                                {visibleRows.map((r) => (
                                    <BillLine
                                        key={r.key}
                                        row={r}
                                        onOpen={(line) => openReceipt(line, r.bookingId)}
                                    />
                                ))}
                            </div>
                        )
                        : <div className="campus-orders-empty">No bills match this view.</div>}
                </>
            )}

            {view === "problems" && (
                <>
                    <div className="campus-table-title">Online with no QR amount — {fmtINR(t.short)}</div>
                    {issues.shortLines.length ? (
                        <div className="campus-table-wrap">
                            <table className="campus-table">
                                <thead>
                                    <tr>
                                        <th>Bill</th>
                                        <th>Customer</th>
                                        <th>Day</th>
                                        <th>What</th>
                                        <th className="r">Amount</th>
                                        <th>Collected by</th>
                                        <th>QR on the receipt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {issues.shortLines.map((l) => {
                                        const row = hisab.rows.find((r) => r.invoice === l.invoice);
                                        return (
                                            <tr key={l.id}>
                                                <td><strong>{l.invoice}</strong></td>
                                                <td>{l.customer}</td>
                                                <td>{fmtDay(l.day)}</td>
                                                <td>{KIND_LABEL[l.kind] || l.kind}</td>
                                                <td className="r"><strong>{fmtINR(l.amount)}</strong></td>
                                                <td>{l.holder || "—"}</td>
                                                <td>
                                                    {l.qrName
                                                        ? <><strong>{l.qrName}</strong><div className="campus-hisab-sub">Name saved, amount not.</div></>
                                                        : (
                                                            <button
                                                                type="button"
                                                                className="campus-paychip-btn"
                                                                onClick={() => openReceipt(l, row?.bookingId)}
                                                            >
                                                                Receipt
                                                            </button>
                                                        )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : <p className="campus-note">Every online payment in this drive is on a QR.</p>}

                    {issues.cashConflicts.length > 0 && (
                        <>
                            <div className="campus-table-title">Marked cash but also on a QR</div>
                            <div className="campus-table-wrap">
                                <table className="campus-table">
                                    <thead>
                                        <tr>
                                            <th>Bill</th>
                                            <th>Customer</th>
                                            <th>Day</th>
                                            <th className="r">Amount</th>
                                            <th>Cash with</th>
                                            <th>Also on QR</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {issues.cashConflicts.map((l) => (
                                            <tr key={l.id}>
                                                <td><strong>{l.invoice}</strong></td>
                                                <td>{l.customer}</td>
                                                <td>{fmtDay(l.day)}</td>
                                                <td className="r"><strong>{fmtINR(l.amount)}</strong></td>
                                                <td>{l.holder || "—"}</td>
                                                <td>{l.alsoQr}{l.alsoQrStaff ? ` · Done by ${l.alsoQrStaff}` : ""}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="campus-note">Either the mode was picked wrong, or one payment was entered twice. Fix it before counting cash in hand.</p>
                        </>
                    )}

                    {issues.zeroDone.length > 0 && (
                        <>
                            <div className="campus-table-title">Done pressed but amount saved as zero</div>
                            <div className="campus-table-wrap">
                                <table className="campus-table">
                                    <thead>
                                        <tr><th>Day</th><th>Bill</th><th>Customer</th><th>QR</th><th>Done by</th></tr>
                                    </thead>
                                    <tbody>
                                        {issues.zeroDone.map((q, i) => (
                                            <tr key={`${q.at}-${i}`}>
                                                <td>{fmtDay(q.day)} {q.clock}</td>
                                                <td>{q.bill || "—"}</td>
                                                <td>{q.customer || "—"}</td>
                                                <td><strong>{q.qrName || "—"}</strong></td>
                                                <td>{q.staffName || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="campus-note">The QR is known for these, only the amount is missing. The bill still shows the app amount as unconfirmed.</p>
                        </>
                    )}

                    {issues.looseQr.length > 0 && (
                        <>
                            <div className="campus-table-title">QR money with no bill — {fmtINR(t.looseQr)}</div>
                            <div className="campus-table-wrap">
                                <table className="campus-table">
                                    <thead>
                                        <tr><th>Day</th><th>Customer</th><th>QR</th><th>Done by</th><th className="r">Amount</th></tr>
                                    </thead>
                                    <tbody>
                                        {issues.looseQr.map((q, i) => (
                                            <tr key={`${q.at}-${i}`}>
                                                <td>{fmtDay(q.day)} {q.clock}</td>
                                                <td>{q.customer || "—"}</td>
                                                <td><strong>{q.qrName || "—"}</strong></td>
                                                <td>{q.staffName || "—"}</td>
                                                <td className="r"><strong>{fmtINR(q.amount)}</strong></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="campus-note">Money reached a QR but no bill in this drive matches the name and amount. Check if the bill was cancelled, edited, or booked on another campus.</p>
                        </>
                    )}

                    {issues.extraQr.length > 0 && (
                        <>
                            <div className="campus-table-title">QR more than the bill needs</div>
                            <div className="campus-table-wrap">
                                <table className="campus-table">
                                    <thead>
                                        <tr><th>Day</th><th>Bill</th><th>Customer</th><th>QR</th><th>Done by</th><th className="r">Amount</th></tr>
                                    </thead>
                                    <tbody>
                                        {issues.extraQr.map((q, i) => (
                                            <tr key={`${q.at}-${i}`}>
                                                <td>{fmtDay(q.day)} {q.clock}</td>
                                                <td>{q.bill || "—"}</td>
                                                <td>{q.customer || "—"}</td>
                                                <td><strong>{q.qrName || "—"}</strong></td>
                                                <td>{q.staffName || "—"}</td>
                                                <td className="r"><strong>{fmtINR(q.amount)}</strong></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="campus-note">Done was pressed twice, or for a bill outside this drive. Possible double entry.</p>
                        </>
                    )}

                    {problemCount === 0 && <p className="campus-note">Nothing to check. Every payment is matched.</p>}
                </>
            )}
            {popup && (
                <ReceiptPopup
                    popup={popup}
                    qrNames={qrNames}
                    saving={savingKey === popup.line.pickKey}
                    onClose={() => setPopup(null)}
                    onPick={savePick}
                />
            )}
        </div>
    );
}
